import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from "@/src/components/ui/chart";

import {
  CHART_CONTAINER_CLASS,
  CHART_MARGIN,
  getValueYAxisProps,
  X_AXIS_PROPS,
} from "./chartLayout";
import { getValueAxisConfig, toChartConfig } from "./charts.utils";
import useTimelineFormatters from "./useTimelineFormatters";

import type { JSX } from "react";

import type {
  ChartSeries,
  ChartValueUnit,
  TimelineDatum,
  TimelineGranularity,
} from "./charts.types";

interface TimelineLineChartProps<TKey extends string> {
  dataList: readonly TimelineDatum<TKey>[];
  seriesList: readonly ChartSeries<TKey>[];
  granularity: TimelineGranularity;
  unit?: ChartValueUnit;
}

function TimelineLineChart<TKey extends string>({
  dataList,
  seriesList,
  granularity,
  unit,
}: TimelineLineChartProps<TKey>): JSX.Element {
  const { i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const config = useMemo(() => toChartConfig(seriesList), [seriesList]);
  const { rowList, valueAxis } = useMemo(() => {
    const keyList = seriesList.map((series) => series.key);
    const maxValue = Math.max(
      0,
      ...dataList.flatMap((datum) => keyList.map((key) => datum.values[key])),
    );
    return {
      rowList: dataList.map((datum) => ({ date: datum.date, ...datum.values })),
      valueAxis: getValueAxisConfig(maxValue, lang, unit),
    };
  }, [dataList, lang, seriesList, unit]);

  const { formatTick, formatAverageTooltipLabel } =
    useTimelineFormatters(granularity);

  return (
    <ChartContainer className={CHART_CONTAINER_CLASS} config={config}>
      <LineChart accessibilityLayer data={rowList} margin={CHART_MARGIN}>
        <CartesianGrid vertical={false} />
        <XAxis
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...X_AXIS_PROPS}
          dataKey="date"
          minTickGap={24}
          tickFormatter={formatTick}
        />
        <YAxis
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...getValueYAxisProps(valueAxis)}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={formatAverageTooltipLabel}
              valueFormatter={valueAxis.formatValue}
            />
          }
        />
        {seriesList.length > 1 ? <ChartLegend /> : null}
        {seriesList.map((series) => (
          <Line
            key={series.key}
            dataKey={series.key}
            dot={rowList.length === 1}
            stroke={`var(--color-${series.key})`}
            strokeWidth={2}
            type="linear"
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}

export default TimelineLineChart;
